import React, { useEffect, useReducer, useState } from "react";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import jwtDecode from "jwt-decode";
import { authApi, tokenStorage } from "components/auth";
import Loading from "components/loading";
import { getMultilangMessage } from "utils/multilang";
import AuthenticationError from "./authentication-error";
import Cookies from "universal-cookie";
import AutoLoginComponent from "./AutoLoginComponent";
import { keycloak } from "config/keycloak";
import authDataStorage from "../auth/auth-data-provider/context/auth-data-storage";

const cookies = new Cookies();
const { storeToken } = tokenStorage;
const { fetchLocale } = authDataStorage;

const keycloakProviderInitOptions = {
  flow: "standard",
  checkLoginIframe: false,
  responseMode: "fragment",
  enableLogging: true,
};

const authenticationInitialState = {
  authenticating: true,
  authenticationError: false,
};

const authenticationReducer = (_, action) => {
  switch (action.type) {
    case "authenticationCompleted":
      return {
        authenticating: false,
        autheticationError: false,
      };

    case "authenticationError":
      return {
        authenticating: false,
        authenticationError: true,
      };

    default:
      throw new Error(`No action type "${action.type}" defined`);
  }
};

const CinfoKeycloakProvider = ({ children }) => {
  const [keycloakToken, setKeycloakToken] = useState(null);
  const [keycloakIdToken, setKeycloakIdToken] = useState(null);
  const [state, dispatch] = useReducer(authenticationReducer, authenticationInitialState);

  useEffect(
    () => {
      if (keycloakToken) {
        const decodedToken = jwtDecode(keycloakToken);
        // Keycloak v26 may not include locale in the token
        // Fallback: stored locale -> browser language -> default 'es'
        // Normalize locale to short format (e.g., "es" instead of "es_ES" or "es-ES")
        const rawLocale = decodedToken.locale || fetchLocale() || navigator.language || "es";
        const locale = rawLocale.split(/[-_]/)[0];

        authApi
          .signIn(keycloakToken, locale)
          .then(token => {
            storeToken(token);
            cookies.set("authToken", token, { path: "/" });
            dispatch({ type: "authenticationCompleted" });
          })
          .catch(() => dispatch({ type: "authenticationError" }));
      }
    },
    [keycloakToken],
  );

  if (state.authenticationError) {
    return <AuthenticationError />;
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      onTokens={({ token, idToken }) => {
        setKeycloakToken(token);
        setKeycloakIdToken(idToken);
        // Store ID token immediately for logout
        if (idToken) {
          cookies.set("keycloakIdToken", idToken, { path: "/" });
        }
      }}
      initOptions={keycloakProviderInitOptions}
    >
      <AutoLoginComponent>
        {state.authenticating ? <Loading message={getMultilangMessage("loggingIn")} /> : children}
      </AutoLoginComponent>
    </ReactKeycloakProvider>
  );
};

export default CinfoKeycloakProvider;
